import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { useQuery } from "convex/react";
import type { AssistantContextUsageEstimate } from "../types";

function clampPercent(value: number) {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function normalizeContextUsageEstimate(
  usage: AssistantContextUsageEstimate | null | undefined
): AssistantContextUsageEstimate | null {
  if (!usage) {
    return null;
  }
  return {
    ...usage,
    estimatedContextPercent: clampPercent(usage.estimatedContextPercent),
  };
}

interface UseAssistantRuntimeActivityArgs {
  projectId: Id<"projects">;
  threadId: string | null;
  latestPromptMessageId: string | null;
  currentRunId?: string | null;
}

/**
 * Live composer state for one Claudia thread: the run the send/stop control acts on, the
 * prompt-execution lifecycle, and the context-usage badge.
 *
 * Backed by `claudia.getThreadRuntimeSnapshot`, a subscription that re-executes on
 * essentially every runtime write while a prompt is running. Whatever this hook reads is
 * paid for ~100x per prompt, so keep the consumed surface minimal and never reintroduce
 * tool-call, artifact or run-event payloads here — the transcript owns that data via
 * `useClaudiaTranscriptItems` (LAT-880).
 */
export function useAssistantRuntimeActivity({
  projectId,
  threadId,
  latestPromptMessageId,
  currentRunId,
}: UseAssistantRuntimeActivityArgs) {
  const snapshot = useQuery(
    api.claudia.getThreadRuntimeSnapshot,
    threadId
      ? {
          projectId,
          threadId,
          latestPromptMessageId: latestPromptMessageId ?? undefined,
          currentRunId: currentRunId ?? undefined,
        }
      : "skip"
  );

  const activeRun = snapshot?.activeRun;
  const latestRun = snapshot?.latestRun;
  const currentRun = snapshot?.currentRun;

  const runtimeRun =
    activeRun ??
    (latestPromptMessageId && latestRun?.promptMessageId === latestPromptMessageId
      ? latestRun
      : (currentRun ?? latestRun ?? null));

  return {
    runtimeRun,
    contextUsage: normalizeContextUsageEstimate(snapshot?.contextUsage),
    promptExecutionStatus: snapshot?.promptStatus,
  };
}
